import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  seesConfidential,
  writeAxisAllows,
  writeAxisOf,
  type MeOrgRef,
  type MeResponse,
  type RunState,
  type WriteAxis,
} from '@ni/shared';
import type { Api } from './client';

/**
 * Auth and org are the app's only two contexts (ui-design §6.2) — everything
 * else is server state in TanStack Query.
 *
 * The org is resolved at bootstrap from `GET /me` and held here. A reader in
 * more than one org picks between them in the nav rail; the pick is remembered
 * in localStorage under `ni.org` and re-validated against `me.orgs` on every
 * `/me` read, so losing membership falls the reader back to their first
 * remaining org and forgets the stored id. SPA routes stay org-less, which is
 * why a switch also resets the route (`org-scope.tsx`).
 */

interface AppState {
  api: Api;
  /**
   * The chosen org **as the membership row describes it** — which is why this is
   * a `MeOrgRef` and not an `OrgRef`: `level` is the caller's in this
   * organization, not a property of the organization, and two colleagues reading
   * the same page answer differently.
   */
  org: MeOrgRef;
  me: MeResponse;
  /** Ignored unless the id names one of `me.orgs`. */
  switchOrg: (orgId: string) => void;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({
  api,
  org,
  me,
  switchOrg,
  children,
}: AppState & { children: ReactNode }) {
  const value = useMemo<AppState>(() => ({ api, org, me, switchOrg }), [api, org, me, switchOrg]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function useApp(): AppState {
  const state = useContext(AppContext);
  if (!state) throw new Error('app context used before bootstrap');
  return state;
}

export const useDemo = (): boolean => useContext(AppContext)?.org.is_demo ?? false;

/**
 * **What this reader may change here**, and the one thing every affordance in
 * this app asks before it renders.
 *
 * A demo organization caps it at `none` for everybody, the same composition the
 * server makes: the two ceilings are independent, the lowest binds, and a
 * dashboard that showed an owner a research button on a demo org would be
 * showing them a 403.
 *
 * **None of this is the fence.** `WriteAccessGuard` is, and it runs whatever
 * this returns. What these hooks buy is a coherent page rather than a wall of
 * refusals — a member who cannot start research sees no button, instead of one
 * that apologizes.
 */
export function useWriteAxis(): WriteAxis {
  const org = useContext(AppContext)?.org;
  /* **No organization, no ceiling** — the same answer the server's
     `confidentialVisibility` gives an absent membership, and for the same
     reason: an absence is not a level. It is unreachable in the running app,
     where `main.tsx` renders no route until an org resolves, and `useDemo`
     beside this reads its absence the same way. The fence is the server's. */
  if (!org) return 'all';
  return org.is_demo ? 'none' : writeAxisOf(org.level);
}

/** Whether this reader may make a write this large. The predicate, pre-applied. */
export function useMayWrite(needed: WriteAxis = 'all'): boolean {
  return writeAxisAllows(useWriteAxis(), needed);
}

/**
 * Whether notes the office marked confidential are shown to this reader (ruled
 * 2026-09-19 and 2026-09-22). The server filters them out of the record
 * regardless; this is for the controls that would offer to *write* one.
 */
export function useSeesConfidential(): boolean {
  const org = useContext(AppContext)?.org;
  return org ? seesConfidential(org.level) : true;
}

export const useApi = (): Api => useApp().api;
export const useOrg = (): MeOrgRef => useApp().org;
export const useMe = (): MeResponse => useApp().me;

/** Everything the switcher needs, and nothing else reads. */
export function useOrgs(): {
  orgs: MeOrgRef[];
  org: MeOrgRef;
  switchOrg: (orgId: string) => void;
} {
  const { me, org, switchOrg } = useApp();
  return { orgs: me.orgs, org, switchOrg };
}

/* ————————————————————————— the resuming window ————————————————————————— */

/**
 * A run whose gating decisions are all answered sits in `awaiting_*` until the
 * continuation invocation actually starts (ui-design §4.3): the answer only
 * enqueues. The strip reads "Resuming…" and keeps its 3s poll for that window,
 * keyed off decisions-answered rather than off a state change.
 *
 * Adjudication has the counter on the polled run to key off; the identity kinds
 * do not, so answering one marks its run here. The mark clears itself when the
 * run leaves the state it was marked in.
 */
interface ResumingState {
  /** Marked against the state it was answered in, so a fork that moves the run
   *  to a *different* awaiting state (none-of-these → awaiting_prose) does not
   *  inherit the mark and does not keep polling. */
  markResuming: (runId: string, state: RunState) => void;
  clearResuming: (runId: string) => void;
  isMarked: (runId: string | null | undefined, state: RunState | null | undefined) => boolean;
}

const ResumingContext = createContext<ResumingState | null>(null);

export function ResumingProvider({ children }: { children: ReactNode }) {
  const [marked, setMarked] = useState<Record<string, RunState>>({});

  const markResuming = useCallback((runId: string, state: RunState) => {
    setMarked((current) => (current[runId] === state ? current : { ...current, [runId]: state }));
  }, []);

  const clearResuming = useCallback((runId: string) => {
    setMarked((current) => {
      if (!current[runId]) return current;
      const next = { ...current };
      delete next[runId];
      return next;
    });
  }, []);

  const isMarked = useCallback(
    (runId: string | null | undefined, state: RunState | null | undefined) =>
      runId && state ? marked[runId] === state : false,
    [marked],
  );

  const value = useMemo<ResumingState>(
    () => ({ markResuming, clearResuming, isMarked }),
    [markResuming, clearResuming, isMarked],
  );

  return <ResumingContext.Provider value={value}>{children}</ResumingContext.Provider>;
}

export function useResuming(): ResumingState {
  const state = useContext(ResumingContext);
  if (!state) throw new Error('useResuming called outside ResumingProvider');
  return state;
}
